<template>
    <div
        v-if="isVisible"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/65"
        data-testid="match-invite-modal"
    >
        <div class="bg-bg border-stroke-1 mx-4 w-full max-w-md border shadow-xl">
            <div class="p-4">
                <div class="mb-4 flex items-center justify-between">
                    <div v-if="!readOnly" class="font-bold text-zinc-400">{{ sentAt }}</div>
                    <div v-else class="flex items-center gap-2 font-bold text-zinc-400">
                        {{ $t('match_invite.game_in_progress') }}
                        <span class="tabular-nums text-white">{{ elapsedTime }}</span>
                    </div>
                    <button @click="close" class="hover:text-titles text-white transition-colors">
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M5.29297 17.293L10.5859 12L5.29297 6.70703L6.70703 5.29297L12 10.5859L17.293 5.29297L18.707 6.70703L13.4141 12L18.707 17.293L17.293 18.707L12 13.4141L6.70703 18.707L5.29297 17.293Z"
                                fill="white"
                            />
                        </svg>
                    </button>
                </div>

                <div class="flex items-center space-x-4">
                    <div class="shrink-0">
                        <div
                            class="border-stroke-1 flex size-16 items-center justify-center border text-2xl"
                        >
                            <Avatar :src="fromUser.avatar" />
                        </div>
                    </div>
                    <div class="flex-1">
                        <p v-if="!readOnly" class="mt-1 text-sm">
                            {{
                                matchtypeName
                                    ? $t('match_invite.invite_text', { name: fromUser.tag_name, matchType: matchtypeName })
                                    : $t('match_invite.invite_text_generic', { name: fromUser.tag_name })
                            }}
                        </p>
                        <p v-else class="mt-1 text-sm flex flex-col">
                            <span class="text-zinc-400">{{ $t('match_invite.game_host') }}</span>
                            <strong>{{ fromUser.tag_name }}</strong>
                        </p>
                    </div>
                </div>

                <p class="my-4 text-center text-lg font-bold text-white uppercase">{{ $t('match_invite.game_details') }}</p>

                <div
                    class="border-stroke-1 flex items-center justify-between border-t py-4 text-sm"
                >
                    <div class="text-left">{{ startingValueLabel }}</div>
                    <div class="text-right font-bold">
                        {{ startingValue }}
                    </div>
                </div>

                <div
                    class="border-stroke-1 flex items-center justify-between border-t py-4 text-sm"
                >
                    <div class="text-left">{{ $t('match_invite.win_by') }}</div>
                    <div class="text-right font-bold">
                        {{ getSettingDisplay(matchData.settings, 'win_type') }}
                    </div>
                </div>

                <div
                    class="border-stroke-1 flex items-center justify-between border-t py-4 text-sm"
                >
                    <div class="text-left">{{ $t('match_invite.sets') }}</div>
                    <div class="text-right font-bold">
                        {{ matchData.settings?.sets_to_win || 1 }}
                    </div>
                </div>

                <div
                    class="border-stroke-1 flex items-center justify-between border-t py-4 text-sm"
                >
                    <div class="text-left">{{ $t('match_invite.legs') }}</div>
                    <div class="text-right font-bold">
                        {{ matchData.settings?.legs_to_win || 1 }}
                    </div>
                </div>

                <div
                    class="border-stroke-1 flex items-center justify-between border-t py-4 text-sm"
                >
                    <div class="text-left">{{ $t('match_invite.throw_out') }}</div>
                    <div class="text-right font-bold">
                        {{ getSettingDisplay(matchData.settings, 'check_out') }}
                    </div>
                </div>

                <div v-if="!readOnly" class="my-4 text-sm">
                    {{ $t('match_invite.setup_question') }} <span class="text-zinc-500">{{ $t('match_invite.setup_optional') }}</span>
                </div>
                <!-- <CustomSelect
                    v-model="selectedSetup"
                    :options="setupOptions"
                    placeholder="Select a setup" -->
                <SetupsSelector v-if="!readOnly" v-model="selectedSetup" />

                <!-- <label class="mt-4 flex cursor-pointer items-center gap-2">
                    <input
                        v-model="blockFutureInvites"
                        type="checkbox"
                        class="h-4 w-4 border-zinc-700 bg-card text-red-600 focus:ring-red-500"
                    />
                    <span class="text-sm text-zinc-300">
                        Don't receive invites from {{ fromUser.tag_name }} anymore
                    </span>
                </label> -->
            </div>

            <div
                v-if="!readOnly"
                class="border-stroke-1 bg-card mt-4 grid grid-cols-2 gap-4 border-t p-4"
            >
                <DiagonalCutButton
                    variant="secondary"
                    cut-position="right-top"
                    data-testid="match-invite-decline-button"
                    @click="decline"
                    :disabled="matchInviteStore.isLoading"
                >
                    <span v-if="matchInviteStore.isLoading && matchInviteStore.action === 'decline'"
                        >{{ $t('match_invite.declining') }}</span
                    >
                    <span v-else>{{ $t('match_invite.decline') }}</span>
                </DiagonalCutButton>
                <DiagonalCutButton
                    variant="primary"
                    cut-position="left-top"
                    data-testid="match-invite-accept-button"
                    @click="accept"
                    :disabled="matchInviteStore.isLoading"
                >
                    <span v-if="matchInviteStore.isLoading && matchInviteStore.action === 'accept'"
                        >{{ $t('match_invite.accepting') }}</span
                    >
                    <span v-else>{{ $t('match_invite.accept') }}</span>
                </DiagonalCutButton>
            </div>
            <div
                v-else
                class="border-stroke-1 bg-card mt-4 grid grid-cols-2 gap-4 border-t p-4"
            >
                <DiagonalCutButton
                    variant="secondary"
                    cut-position="right-top"
                    @click="$emit('leave')"
                    :disabled="isLeaving"
                >
                    <span v-if="isLeaving">{{ $t('match_invite.leaving_game') }}</span>
                    <span v-else>{{ $t('match_invite.leave_game') }}</span>
                </DiagonalCutButton>
                <DiagonalCutButton
                    variant="primary"
                    cut-position="left-top"
                    @click="close"
                    :disabled="isLeaving"
                >
                    {{ $t('general.close') }}
                </DiagonalCutButton>
            </div>
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useMatchInviteStore } from '@/stores/matchInvite.js'
import { useAuthStore } from '@/stores/auth.js'
import { getSettingDisplay as formatSettingDisplay } from '@/utils/settingsHelpers.js'
import { formatRelativeTime } from '@/utils/dateHelpers.js'
import { matchTypeName } from '@/constants/matchTypes.js'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import Avatar from './Avatar.vue'
import SetupsSelector from './ui/setups/SetupsSelector.vue'

export default {
    name: 'MatchInviteModal',
    components: { DiagonalCutButton, Avatar, SetupsSelector },
    props: {
        invite: {
            type: Object,
            default: null,
        },
        isVisible: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLeaving: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'accept-success',
        'accept-error',
        'decline-success',
        'decline-error',
        'close',
        'leave',
    ],
    data() {
        return {
            selectedSetup: null,
            blockFutureInvites: false,
            now: Date.now(),
            elapsedTimer: null,
        }
    },
    computed: {
        ...mapStores(useMatchInviteStore, useAuthStore),

        fromUser() {
            return this.invite?.data?.from_user || this.invite?.from_user || {}
        },

        matchData() {
            return this.invite?.data?.match || this.invite?.match || {}
        },

        matchtypeName() {
            return matchTypeName(this.matchData.matchtype_id, this.matchData.matchtype_name || null)
        },

        sentAt() {
            const sentDate = this.invite?.data?.sent_at || this.invite?.sent_at
            return sentDate ? formatRelativeTime(sentDate) : this.$t('match_invite.just_now')
        },

        elapsedTime() {
            const startDate = this.invite?.data?.started_at || this.invite?.started_at
            if (!startDate) return '00:00'

            const startMs = new Date(startDate).getTime()
            if (Number.isNaN(startMs)) return '00:00'

            let diff = Math.max(0, Math.floor((this.now - startMs) / 1000))
            const hours = Math.floor(diff / 3600)
            diff %= 3600
            const minutes = Math.floor(diff / 60)
            const seconds = diff % 60
            const pad = (value) => String(value).padStart(2, '0')

            return hours > 0
                ? `${hours}:${pad(minutes)}:${pad(seconds)}`
                : `${pad(minutes)}:${pad(seconds)}`
        },

        isTacticsGame() {
            const processorName =
                this.matchData.matchtype?.processor_name || this.matchData.processor_name
            return processorName === 'tactics'
        },

        startingValueLabel() {
            return this.isTacticsGame ? this.$t('match_invite.lower_bound') : this.$t('match_invite.starting_score')
        },

        startingValue() {
            return this.isTacticsGame
                ? this.matchData.settings?.lower_bound
                : this.matchData.settings?.starting_score
        },

        setupOptions() {
            return this.authStore.dart_setups.map((setup) => ({
                value: setup.id,
                label: setup.name,
            }))
        },
    },
    watch: {
        isVisible: {
            immediate: true,
            handler(visible) {
                if (visible && this.readOnly) {
                    this.startElapsedTimer()
                } else {
                    this.stopElapsedTimer()
                }
            },
        },
    },
    beforeUnmount() {
        this.stopElapsedTimer()
    },
    methods: {
        startElapsedTimer() {
            this.now = Date.now()
            if (this.elapsedTimer) return
            this.elapsedTimer = setInterval(() => {
                this.now = Date.now()
            }, 1000)
        },
        stopElapsedTimer() {
            if (this.elapsedTimer) {
                clearInterval(this.elapsedTimer)
                this.elapsedTimer = null
            }
        },
        getSettingDisplay(settings, key) {
            return formatSettingDisplay(settings, key)
        },
        async accept() {
            const playerName = this.fromUser.tag_name || this.$t('match_invite.your_friend')
            const invite = this.invite
            const isOnlineMatch = Boolean(this.matchData.online)

            try {
                await this.matchInviteStore.acceptInvite(
                    this.selectedSetup,
                    this.blockFutureInvites,
                )

                this.$toast(
                    this.$t('match_invite.accept'),
                    8000,
                    isOnlineMatch
                        ? this.$t('match_invite.joined_lobby_toast', { name: playerName })
                        : this.$t('match_invite.accepted_toast', { name: playerName }),
                )

                this.$emit('accept-success', invite)
            } catch (error) {
                console.error('Failed to accept invite:', error)
                this.$emit('accept-error', error)
            }
        },
        async decline() {
            const playerName = this.fromUser.tag_name || this.$t('match_invite.that_player')

            try {
                await this.matchInviteStore.declineInvite(this.blockFutureInvites)

                if (this.blockFutureInvites) {
                    this.$toast(
                        this.$t('match_invite.decline'),
                        5000,
                        this.$t('match_invite.declined_toast', { name: playerName }),
                    )
                }

                this.$emit('decline-success', this.invite)
            } catch (error) {
                console.error('Failed to decline invite:', error)
                this.$emit('decline-error', error)
            }
        },
        close() {
            if (!this.readOnly) {
                this.matchInviteStore.closeModal()
            }
            this.$emit('close')
        },
    },
}
</script>
